import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Settings } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface TavilyExtractConfig {
  id: string;
  nombre: string;
  endpoint: string;
  auth_token: string;
  include_images: boolean;
  extract_depth: 'basic' | 'advanced';
  created_at: string;
}

export default function TavilyExtractConfig() {
  const [configs, setConfigs] = useState<TavilyExtractConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TavilyExtractConfig | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newConfig, setNewConfig] = useState({
    nombre: '',
    endpoint: 'https://api.tavily.com/extract',
    auth_token: '',
    include_images: false,
    extract_depth: 'basic' as const
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('tavily_extract_configs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setConfigs(data || []);
    } catch (error: any) {
      console.error('Error fetching Tavily Extract configs:', error);
      setError('Error al cargar las configuraciones');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingConfig) {
        const { error } = await supabase
          .from('tavily_extract_configs')
          .update(newConfig)
          .eq('id', editingConfig.id);

        if (error) throw error;
        setSuccess('Configuración actualizada correctamente');
      } else {
        const { error } = await supabase
          .from('tavily_extract_configs')
          .insert([newConfig]);

        if (error) throw error;
        setSuccess('Configuración creada correctamente');
      }

      setShowModal(false);
      setEditingConfig(null);
      setNewConfig({
        nombre: '',
        endpoint: 'https://api.tavily.com/extract',
        auth_token: '',
        include_images: false,
        extract_depth: 'basic'
      });
      fetchConfigs();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleEdit = (config: TavilyExtractConfig) => {
    setEditingConfig(config);
    setNewConfig({
      nombre: config.nombre,
      endpoint: config.endpoint,
      auth_token: config.auth_token,
      include_images: config.include_images,
      extract_depth: config.extract_depth
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar esta configuración?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('tavily_extract_configs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Configuración eliminada correctamente');
      fetchConfigs();
    } catch (error: any) {
      setError(error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">API Tavily Extract</h1>
        <button 
          onClick={() => {
            setEditingConfig(null);
            setNewConfig({
              nombre: '',
              endpoint: 'https://api.tavily.com/extract',
              auth_token: '',
              include_images: false,
              extract_depth: 'basic'
            });
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Configuración
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="flex-1 bg-white rounded-lg shadow-md overflow-hidden flex flex-col">
        <div className="p-6 border-b">
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar configuraciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left">
                <th className="py-3 px-6 font-semibold text-gray-600">Nombre</th>
                <th className="py-3 px-6 font-semibold text-gray-600">Endpoint</th>
                <th className="py-3 px-6 font-semibold text-gray-600">Extract Depth</th>
                <th className="py-3 px-6 font-semibold text-gray-600">Include Images</th>
                <th className="py-3 px-6 font-semibold text-gray-600">Creado</th>
                <th className="py-3 px-6 font-semibold text-gray-600">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {configs.map((config) => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="py-4 px-6">
                    <div className="flex items-center">
                      <Settings className="w-5 h-5 text-blue-500 mr-2" />
                      {config.nombre}
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <div className="max-w-xs truncate">
                      {config.endpoint}
                    </div>
                  </td>
                  <td className="py-4 px-6">{config.extract_depth}</td>
                  <td className="py-4 px-6">
                    {config.include_images ? 'Sí' : 'No'}
                  </td>
                  <td className="py-4 px-6">
                    {new Date(config.created_at).toLocaleDateString()}
                  </td>
                  <td className="py-4 px-6">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(config)}
                        className="p-1 text-blue-500 hover:bg-blue-100 rounded-full"
                        title="Editar configuración"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        className="p-1 text-red-500 hover:bg-red-100 rounded-full"
                        title="Eliminar configuración"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {configs.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">
                    No hay configuraciones disponibles
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para añadir/editar configuración */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  required
                  value={newConfig.nombre}
                  onChange={(e) => setNewConfig({...newConfig, nombre: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Nombre de la configuración"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endpoint
                </label>
                <input
                  type="text"
                  required
                  value={newConfig.endpoint}
                  onChange={(e) => setNewConfig({...newConfig, endpoint: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="https://api.tavily.com/extract"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authorization Token
                </label>
                <input
                  type="password"
                  required
                  value={newConfig.auth_token}
                  onChange={(e) => setNewConfig({...newConfig, auth_token: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Bearer tvly-YOUR_API_KEY"
                />
                <p className="mt-1 text-sm text-gray-500">
                  Formato: Bearer tvly-YOUR_API_KEY
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Extract Depth
                </label>
                <select
                  value={newConfig.extract_depth}
                  onChange={(e) => setNewConfig({...newConfig, extract_depth: e.target.value as 'basic' | 'advanced'})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="basic">Basic</option>
                  <option value="advanced">Advanced</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Advanced extraction retrieves more data but costs more credits
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newConfig.include_images}
                    onChange={(e) => setNewConfig({...newConfig, include_images: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Include Images</span>
                </label>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingConfig(null);
                  }}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingConfig ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}